var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '../styles/sidebar.scss';
import { DataContext } from '../player/DataContext';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useMemo, useRef } from 'react';
import ButtonGroup from './ButtonGroup';
import { useOnResize } from '../helper/hooks/onResize';
import globalStore from '../store';
import SettingWithPopup from './SettingWithPopup';
import AudioButton from './AudioButton';
import HelpTool from './HelpTool';
import customEventHandler from '../helper/customEvents';
import { useRovingTabIndex } from '../helper/hooks/useRovingTabIndex';
import CustomScrollbar from './CustomScrollbar';
// const { zoomLevel, playerIconZoom } = globalStore.useStoreState(state => state.playerStore);
// Reliable touch-primary detection — works on modern iPads (desktop UA) unlike browserDetect
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var Sidebar = function (props) {
    var _a, _b, _c, _d, _e;
    var t = useTranslation().t;
    var _f = globalStore.useStoreState(function (state) { return state.playerStore; }), displayMode = _f.displayMode, currentPage = _f.currentPage, totalPages = _f.totalPages, playerIconZoom = _f.playerIconZoom, viewEditMode = _f.viewEditMode;
    var _g = props.collapsibleGuide, collapsibleGuideProp = _g === void 0 ? {} : _g, _h = props.disabledButtons, disabledButtons = _h === void 0 ? [] : _h;
    var _j = useContext(DataContext), selectedTool = _j.selectedTool, manipulative = _j.manipulative;
    // In view mode, hide 'edit' (configure) and 'lock' inside toolsCollapsible instead of hiding the entire toolbox
    var viewModeCollapsibleHidden = viewEditMode === 'view' ? ['edit', 'lock'] : [];
    var collapsibleGuide = useMemo(function () {
        if (viewModeCollapsibleHidden.length === 0)
            return collapsibleGuideProp;
        var existing = collapsibleGuideProp.toolsCollapsible || {};
        var mergedHidden = Array.from(new Set(__spreadArray(__spreadArray([], (existing.hiddenElements || []), true), viewModeCollapsibleHidden, true)));
        return __assign(__assign({}, collapsibleGuideProp), { toolsCollapsible: __assign(__assign({}, existing), { hiddenElements: mergedHidden }) });
    }, [collapsibleGuideProp, viewEditMode]);
    // In view mode, only hide the entire toolsCollapsible if ALL its elements would be hidden
    var viewModeHiddenButtons = useMemo(function () {
        var _a;
        var hidden = [];
        if (viewEditMode !== 'view')
            return hidden;
        // Check if all toolsCollapsible elements are hidden
        var toolsElements = ['edit', 'group', 'show5x5Grid', 'duplicate', 'lock', 'scale'];
        var hiddenInCollapsible = ((_a = collapsibleGuide.toolsCollapsible) === null || _a === void 0 ? void 0 : _a.hiddenElements) || [];
        var allHidden = toolsElements.every(function (el) { return hiddenInCollapsible.includes(el); });
        if (allHidden) {
            hidden.push('toolsCollapsible');
        }
        return hidden;
    }, [viewEditMode, collapsibleGuide]);
    var setDisplayMode = globalStore.useStoreActions(function (state) { return state.playerStore; }).setDisplayMode;
    var mainConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).mainConfig;
    var sideBottomRef = useRef(null);
    var sideRef = useRef(null);
    var uiData = mainConfig === null || mainConfig === void 0 ? void 0 : mainConfig.sidebarBtn;
    // Fix for Safari/Firefox: flex column-wrap doesn't expand container width.
    // Calculate number of columns and set explicit width (same approach as RightSideToolPanel).
    var calculateSidebarWidth = function () {
        var sidebar = sideRef.current;
        if (!sidebar)
            return;
        var sidebarTop = sidebar.querySelector('.sidebarTop');
        if (!sidebarTop)
            return;
        // Measure actual icon size from a rendered button
        var firstBtn = sidebarTop.querySelector('button');
        if (!firstBtn)
            return;
        var iconSize = firstBtn.getBoundingClientRect().width || 45;
        var padding = 20; // sidebar padding: 10px each side
        if (!IS_TOUCH_DEVICE) {
            // Check if custom scrollbar is visible (desktop only)
            var scrollContent = sidebarTop.querySelector('.customScrollbarContent');
            var isScrollbarVisible = scrollContent
                ? scrollContent.scrollHeight > scrollContent.clientHeight
                : false;
            var scrollbarExtra = isScrollbarVisible ? 15 : 0;
            var sidebarTopInner = sidebarTop.querySelector('.sidebarTopInner');
            if (sidebarTopInner) {
                sidebarTopInner.style.paddingRight = isScrollbarVisible ? '20px' : '0px';
            }
            var contentWidth = iconSize + padding + scrollbarExtra;
            sidebar.style.width = "".concat(Math.ceil(contentWidth), "px");
            sidebarTop.style.width = "".concat(Math.ceil(contentWidth - padding), "px");
            sidebarTop.style.minWidth = "".concat(Math.ceil(contentWidth - padding), "px");
        }
        else {
            var contentWidth = iconSize + padding;
            sidebar.style.width = "".concat(Math.ceil(contentWidth), "px");
            sidebarTop.style.width = "".concat(Math.ceil(contentWidth - padding), "px");
            sidebarTop.style.minWidth = "".concat(Math.ceil(contentWidth - padding), "px");
        }
    };
    useOnResize(calculateSidebarWidth);
    useEffect(function () {
        var timeoutId = setTimeout(function () {
            requestAnimationFrame(calculateSidebarWidth);
        }, 50);
        return function () { return clearTimeout(timeoutId); };
    }, [playerIconZoom, uiData]);
    // Desktop only: roving tabindex for keyboard navigation
    var topButtonCount = (_c = (_b = (_a = uiData === null || uiData === void 0 ? void 0 : uiData.topBtns) === null || _a === void 0 ? void 0 : _a.upTop) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
    var topRoving = useRovingTabIndex({
        itemCount: IS_TOUCH_DEVICE ? 0 : topButtonCount,
        orientation: 'vertical',
    });
    var bottomRoving = useRovingTabIndex({
        itemCount: IS_TOUCH_DEVICE ? 0 : 3,
        orientation: 'vertical',
    });
    var buttonGroupContent = (_jsx(ButtonGroup, __assign({ align: "vertical", buttonsInfo: (_e = (_d = uiData === null || uiData === void 0 ? void 0 : uiData.topBtns) === null || _d === void 0 ? void 0 : _d.upTop) !== null && _e !== void 0 ? _e : [], mainClass: "sidebarTop gapContentWrapper", type: "sidebarBtn" }, (!IS_TOUCH_DEVICE && {
        rovingGetTabIndex: topRoving.getTabIndex,
        rovingSetItemRef: topRoving.setItemRef,
        rovingStartIndex: 0,
    }), { onClick: function (id, type, data) {
            if (id === 'delete') {
                customEventHandler.dispatchEvent('bottomPanel', { id: 'delete' });
            }
            else {
                customEventHandler.dispatchEvent('sidebarClicked', { id: id, type: type, data: data });
            }
        }, buttonStyleType: "imageType1", collapsibleGuide: collapsibleGuide, disabledButtons: disabledButtons, hiddenButtons: viewModeHiddenButtons, useViewportPosition: !IS_TOUCH_DEVICE && displayMode === 'normal' })));
    return (_jsx(_Fragment, { children: uiData &&
            (IS_TOUCH_DEVICE ? (
            /* ── TOUCH DEVICES: custom scrollbar for overflow ── */
            _jsxs("nav", { className: "sidebar", "aria-label": t('text.toolsSidebar'), ref: sideRef, tabIndex: -1, children: [_jsx("div", { className: "sidebarTop sidebarTop--touch", children: _jsx(CustomScrollbar, { direction: "vertical", tabIndex: -1, children: buttonGroupContent }) }), _jsx("div", { className: "sidebarBottom gapContentWrapper", children: _jsxs("div", { className: "sidebarlastBtns", ref: sideBottomRef, children: [_jsx(HelpTool, {}), _jsx(AudioButton, {}), _jsx(SettingWithPopup, {})] }) })] })) : (
            /* ── DESKTOP: custom scrollbar + full accessibility ── */
            _jsxs("nav", { className: "sidebar", "aria-label": t('text.toolsSidebar'), ref: sideRef, tabIndex: -1, children: [_jsx("div", { className: "sidebarTop", role: "toolbar", "aria-label": t('text.drawingTools'), "aria-orientation": "vertical", ref: function (el) {
                            topRoving.groupProps.ref(el);
                        }, tabIndex: topRoving.groupProps.tabIndex, onKeyDown: topRoving.groupProps.onKeyDown, onFocus: topRoving.groupProps.onFocus, children: _jsx(CustomScrollbar, { direction: "vertical", tabIndex: -1, children: buttonGroupContent }) }), _jsx("div", __assign({ className: "sidebarBottom gapContentWrapper", role: "toolbar", "aria-label": t('text.utilities'), "aria-orientation": "vertical" }, bottomRoving.groupProps, { children: _jsxs("div", { className: "sidebarlastBtns", ref: sideBottomRef, children: [_jsx("div", { ref: bottomRoving.setItemRef(0), children: _jsx(HelpTool, {}) }), _jsx("div", { ref: bottomRoving.setItemRef(1), children: _jsx(AudioButton, {}) }), _jsx("div", { ref: bottomRoving.setItemRef(2), children: _jsx(SettingWithPopup, {}) })] }) }))] }))) }));
};
export default Sidebar;
